/**
 * AgendaTimeline — vista presentacional (solo lectura) de la agenda de un evento.
 *
 * Pensada para usarse DURANTE el campamento desde el celular:
 *  - Tarjeta "AHORA / SIGUE" que marca la actividad en curso y la próxima,
 *    con cuenta regresiva y barra de progreso.
 *  - Selector de días (chips) con "HOY" automático.
 *  - Línea de tiempo por día, con cada actividad categorizada (culto, comida,
 *    deporte, descanso, examen, ceremonia, reunión) por color + ícono.
 *  - Bloques pasados atenuados; el actual resaltado con anillo y pulso.
 *
 * Sin dependencias de auth/firebase → se usa igual en la página pública
 * (/agenda/:id) y dentro del evento (EventAgendaPanel).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Clock,
  Utensils,
  Trophy,
  Waves,
  Moon,
  Sun,
  BookOpen,
  Flag,
  HeartHandshake,
  ClipboardList,
  Tent,
  Users,
  MapPin,
  CalendarDays,
  Radio,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";

export interface AgendaBlock {
  id: string;
  startTime: string;
  endTime: string;
  title: string;
  description?: string;
  location?: string;
}
export interface AgendaDay {
  id: string;
  date: string; // YYYY-MM-DD
  dayCoordinator?: string;
  blocks: AgendaBlock[];
}

// ===== Categorización de actividades =====
// Las 5 categorías oficiales del boletín ASORIENTE 2026 + 3 utilitarias para los
// bloques de vida diaria (comida/descanso/general) que no son competencias.
type CatKey =
  | "mision" // 🟠 Preparación y Misión
  | "espiritual" // 🟣 Espirituales
  | "formacion" // ⚪ Formación
  | "deportivo" // ⚫ Deportivos
  | "puntaje" // 🟢 Prerrequisitos y Puntaje General
  | "comida"
  | "descanso"
  | "general";

interface Cat {
  label: string;
  Icon: LucideIcon;
  /** ¿es una de las 5 categorías oficiales del boletín? (para la leyenda) */
  oficial?: boolean;
  /** fondo + texto del chip de categoría */
  chip: string;
  /** color de la barra/raíl y el punto */
  bar: string;
  /** fondo suave de la tarjeta cuando está EN CURSO */
  liveBg: string;
  /** anillo cuando está EN CURSO */
  ring: string;
}

const CATS: Record<CatKey, Cat> = {
  mision: { label: "Preparación y Misión", oficial: true, Icon: HeartHandshake, chip: "bg-orange-100 text-orange-700", bar: "bg-orange-500", liveBg: "bg-orange-50", ring: "ring-orange-400" },
  espiritual: { label: "Espiritual", oficial: true, Icon: BookOpen, chip: "bg-violet-100 text-violet-700", bar: "bg-violet-500", liveBg: "bg-violet-50", ring: "ring-violet-400" },
  formacion: { label: "Formación", oficial: true, Icon: Flag, chip: "bg-slate-200 text-slate-700", bar: "bg-slate-500", liveBg: "bg-slate-50", ring: "ring-slate-400" },
  deportivo: { label: "Deportivo", oficial: true, Icon: Trophy, chip: "bg-gray-800 text-white", bar: "bg-gray-800", liveBg: "bg-gray-100", ring: "ring-gray-500" },
  puntaje: { label: "Puntaje General", oficial: true, Icon: ClipboardList, chip: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500", liveBg: "bg-emerald-50", ring: "ring-emerald-400" },
  comida: { label: "Comida", Icon: Utensils, chip: "bg-amber-100 text-amber-700", bar: "bg-amber-500", liveBg: "bg-amber-50", ring: "ring-amber-400" },
  descanso: { label: "Descanso", Icon: Moon, chip: "bg-sky-100 text-sky-700", bar: "bg-sky-500", liveBg: "bg-sky-50", ring: "ring-sky-400" },
  general: { label: "General", Icon: Tent, chip: "bg-stone-100 text-stone-600", bar: "bg-stone-400", liveBg: "bg-stone-50", ring: "ring-stone-400" },
};

function categorize(title: string): { cat: Cat; key: CatKey; Icon: LucideIcon } {
  const t = title.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const has = (re: RegExp) => re.test(t);
  let key: CatKey = "general";
  if (has(/cena del senor/)) key = "espiritual";
  else if (has(/culto|devoci|bautismo|santo sabado|despedida de sabado|oraci|matutina|conexion biblica|escuela sab|reavivados|en esto creo|en mi corazon|dichos|\breyes\b|notas y letras|recepcion de santo|inauguraci|clausura/))
    key = "espiritual";
  else if (has(/ultimate|pickle|juegos criollos|\bjuego|voleibol|nataci|prueba de conocimiento|destreza|habilidad|deportiv|triatl|futbol/))
    key = "deportivo";
  else if (has(/uniforme|conservaci|portada|disciplina|inspeccion|revision|parada|marcha/)) key = "formacion";
  else if (has(/conquistador por un dia|de manana te buscare|amigo de la humanidad|testificacion|evangelismo|\bmision/)) key = "mision";
  else if (has(/informe|carpeta|documentacion|organizacion y cumplimiento|video de evento|puntaje/)) key = "puntaje";
  else if (has(/desayuno|almuerzo|\bcena\b|comida|refrigerio|merienda/)) key = "comida";
  else if (has(/levantada|aseo|descanso|libre|ubicaci|dormir|piscina/)) key = "descanso";
  // íconos especiales
  let Icon = CATS[key].Icon;
  if (key === "deportivo" && has(/nataci/)) Icon = Waves;
  if (key === "descanso" && has(/levantada|aseo/)) Icon = Sun;
  return { cat: CATS[key], key, Icon };
}

// ===== Tiempo =====
const toTs = (date: string, hhmm: string): number => {
  const [y, m, d] = date.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d, h || 0, mi || 0).getTime();
};
const dayLabel = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  if (!y) return date;
  const s = new Intl.DateTimeFormat("es-CO", { weekday: "long", day: "numeric", month: "long" }).format(new Date(y, m - 1, d));
  return s.charAt(0).toUpperCase() + s.slice(1); // solo la inicial (no cada palabra)
};
const chipLabel = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  if (!y) return { wd: "", d: date };
  const dt = new Date(y, m - 1, d);
  return { wd: new Intl.DateTimeFormat("es-CO", { weekday: "short" }).format(dt).replace(".", ""), d: String(d) };
};
const fmtCountdown = (ms: number) => {
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};
const todayYMD = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function AgendaTimeline({ days }: { days: AgendaDay[] }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const sortedDays = useMemo(
    () =>
      [...days]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((d) => ({ ...d, blocks: [...d.blocks].sort((x, y) => x.startTime.localeCompare(y.startTime)) })),
    [days],
  );

  // Aplana bloques con timestamps absolutos
  const flat = useMemo(
    () =>
      sortedDays.flatMap((d) =>
        d.blocks.map((b) => ({ block: b, date: d.date, start: toTs(d.date, b.startTime), end: toTs(d.date, b.endTime) })),
      ),
    [sortedDays],
  );

  // Bloque actual (el de inicio más reciente que cubre "ahora") y el siguiente
  const current = useMemo(() => {
    const covering = flat.filter((f) => now >= f.start && now < f.end).sort((a, b) => b.start - a.start);
    return covering[0] || null;
  }, [flat, now]);
  const next = useMemo(() => flat.filter((f) => f.start > now).sort((a, b) => a.start - b.start)[0] || null, [flat, now]);

  // Día seleccionado: hoy si está en el rango, si no el del próximo bloque, si no el primero.
  const tymd = todayYMD(new Date(now));
  const defaultDate = useMemo(() => {
    if (sortedDays.some((d) => d.date === tymd)) return tymd;
    if (next) return next.date;
    if (current) return current.date;
    return sortedDays[0]?.date ?? "";
  }, [sortedDays, tymd, next, current]);
  const [selected, setSelected] = useState(defaultDate);
  useEffect(() => setSelected(defaultDate), [defaultDate]);

  if (sortedDays.length === 0) return null;
  const day = sortedDays.find((d) => d.date === selected) || sortedDays[0];

  return (
    <div className="space-y-4">
      {/* ===== Hero: AHORA / SIGUE ===== */}
      <NowNextHero current={current} next={next} now={now} onJump={(date) => setSelected(date)} />

      {/* ===== Chips de días (sticky) ===== */}
      <div className="sticky top-0 z-10 -mx-1 px-1 py-2 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {sortedDays.map((d) => {
            const isSel = d.date === selected;
            const isToday = d.date === tymd;
            const { wd, d: dn } = chipLabel(d.date);
            return (
              <button
                key={d.id}
                onClick={() => setSelected(d.date)}
                className={`flex-shrink-0 flex flex-col items-center justify-center w-[3.6rem] h-[3.6rem] rounded-2xl border-2 transition-all ${
                  isSel
                    ? "border-purple-600 bg-purple-600 text-white shadow-md shadow-purple-500/25"
                    : "border-gray-200 bg-white text-gray-600 hover:border-purple-300"
                }`}
              >
                <span className="text-[10px] font-medium uppercase capitalize leading-none">{wd}</span>
                <span className="text-lg font-bold leading-tight">{dn}</span>
                {isToday && <span className={`w-1.5 h-1.5 rounded-full ${isSel ? "bg-white" : "bg-purple-500"}`} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== Día seleccionado ===== */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-purple-600" />
            {dayLabel(day.date)}
          </h3>
          {day.date === tymd && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-600 text-white px-2 py-1 rounded-full">Hoy</span>
          )}
        </div>
        {day.dayCoordinator && (
          <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            <span className="font-medium text-gray-600">Coordinación:</span> {day.dayCoordinator}
          </p>
        )}

        {day.blocks.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-6 text-center">Sin actividades programadas este día.</p>
        ) : (
          <ol className="space-y-2.5">
            {day.blocks.map((b) => {
              const start = toTs(day.date, b.startTime);
              const end = toTs(day.date, b.endTime);
              const live = now >= start && now < end;
              const past = end <= now;
              const { cat, Icon } = categorize(b.title);
              const pct = live ? Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100)) : 0;
              return (
                <li
                  key={b.id}
                  className={`relative rounded-2xl border bg-white transition-all ${
                    live ? `${cat.liveBg} border-transparent ring-2 ${cat.ring} shadow-md` : "border-gray-100"
                  } ${past ? "opacity-55" : ""}`}
                >
                  <div className="flex gap-3 p-3">
                    {/* Hora + raíl */}
                    <div className="flex-shrink-0 w-14 text-center">
                      <div className={`text-sm font-bold tabular-nums ${live ? "text-gray-900" : "text-gray-700"}`}>{b.startTime}</div>
                      <div className="text-[11px] text-gray-400 tabular-nums">{b.endTime}</div>
                      <div className={`mx-auto mt-1.5 w-7 h-7 rounded-full flex items-center justify-center ${cat.chip}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                    </div>
                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-gray-900 text-[15px] leading-snug">{b.title}</h4>
                        {live && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-purple-600 text-white px-2 py-0.5 rounded-full animate-pulse">
                            <Radio className="w-3 h-3" /> Ahora
                          </span>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${cat.chip}`}>
                        {cat.label}
                      </span>
                      {b.description && <p className="text-[13px] text-gray-600 mt-1.5 whitespace-pre-line">{b.description}</p>}
                      {b.location && (
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {b.location}
                        </p>
                      )}
                      {live && (
                        <div className="mt-2">
                          <div className="h-1.5 rounded-full bg-white/70 overflow-hidden">
                            <div className={`h-full rounded-full ${cat.bar} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">Termina a las {b.endTime} · faltan {fmtCountdown(end - now)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* ===== Leyenda: 5 categorías oficiales del boletín ===== */}
      <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">Categorías del campamento</p>
        <div className="flex flex-wrap gap-1.5">
          {(["mision", "espiritual", "formacion", "deportivo", "puntaje"] as CatKey[]).map((k) => {
            const c = CATS[k];
            return (
              <span key={k} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg ${c.chip}`}>
                <c.Icon className="w-3 h-3" />
                {c.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ===== Tarjeta AHORA / SIGUE =====
function NowNextHero({
  current,
  next,
  now,
  onJump,
}: {
  current: { block: AgendaBlock; date: string; start: number; end: number } | null;
  next: { block: AgendaBlock; date: string; start: number; end: number } | null;
  now: number;
  onJump: (date: string) => void;
}) {
  if (current) {
    const { cat, Icon } = categorize(current.block.title);
    const pct = Math.min(100, Math.max(0, ((now - current.start) / (current.end - current.start)) * 100));
    return (
      <div className="rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white p-4 shadow-lg shadow-purple-500/20">
        <div className="flex items-center gap-2 text-purple-200 text-[11px] font-bold uppercase tracking-wider mb-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          Sucediendo ahora
        </div>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold leading-tight">{current.block.title}</h2>
            <p className="text-purple-100 text-sm mt-0.5">
              {current.block.startTime}–{current.block.endTime} · {cat.label}
            </p>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white/20 overflow-hidden">
          <div className="h-full rounded-full bg-white transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-purple-100 text-xs mt-1.5">Faltan {fmtCountdown(current.end - now)} para que termine</p>
        {next && (
          <button
            onClick={() => onJump(next.date)}
            className="mt-3 w-full flex items-center justify-between gap-2 rounded-xl bg-white/10 hover:bg-white/15 px-3 py-2 text-left transition-colors"
          >
            <span className="text-sm">
              <span className="text-purple-200 text-xs font-semibold uppercase tracking-wide">Luego · {next.block.startTime}</span>
              <span className="block font-medium truncate">{next.block.title}</span>
            </span>
            <ArrowRight className="w-4 h-4 flex-shrink-0 text-purple-200" />
          </button>
        )}
      </div>
    );
  }

  if (next) {
    const { cat, Icon } = categorize(next.block.title);
    const sameDay = todayYMD(new Date(now)) === next.date;
    return (
      <button
        onClick={() => onJump(next.date)}
        className="w-full text-left rounded-2xl border-2 border-purple-200 bg-purple-50 p-4 hover:bg-purple-100/60 transition-colors"
      >
        <div className="flex items-center gap-2 text-purple-600 text-[11px] font-bold uppercase tracking-wider mb-1.5">
          <Clock className="w-3.5 h-3.5" /> A continuación
        </div>
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${cat.chip}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 leading-tight">{next.block.title}</h2>
            <p className="text-gray-500 text-sm mt-0.5">
              {sameDay ? "Hoy" : dayLabel(next.date)} · {next.block.startTime} · en {fmtCountdown(next.start - now)}
            </p>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-gray-100 bg-white p-4 text-center">
      <Trophy className="w-7 h-7 text-amber-400 mx-auto mb-1.5" />
      <p className="font-semibold text-gray-700">El campamento finalizó</p>
      <p className="text-xs text-gray-400 mt-0.5">¡Gracias por acompañarnos!</p>
    </div>
  );
}
